import { useState } from "react";

const faqs = [
  {
    category: "Dashboard",
    question: "What does the installer dashboard include?",
    answer:
      "Your dashboard has four sections: Available Leads (the live marketplace), Purchased Leads (your pipeline), Billing (transaction history), and Settings (service area preferences). Everything you need to find, buy, and manage leads is in one place — no external tools required.",
  },
  {
    category: "Lead Scoring",
    question: "What is the Lead Score and Solar Score?",
    answer:
      "Every lead has two scores from 50 to 99. The Lead Score reflects the quality of the homeowner — factors like homeownership status, monthly bill size, and purchase timeline. The Solar Score reflects the property's solar potential based on location, roof condition, and sunlight hours. Higher scores on both = better opportunity. You can filter the marketplace by score range to only see leads that meet your standards.",
  },
  {
    category: "Lead Scoring",
    question: "What score threshold should I set?",
    answer:
      "In your Settings, you can set a minimum lead score from 50 to 99. A score of 75+ gives you a solid balance of volume and quality. Setting 90+ filters to elite leads only — fewer available but much higher close rates. You can adjust this anytime from your dashboard settings.",
  },
  {
    category: "Purchase Process",
    question: "How does the 5-minute reservation work?",
    answer:
      "When you click 'Buy Lead', the lead is instantly reserved for you for 5 minutes while you complete Stripe checkout. During that window, no other installer can purchase it. If you don't complete payment within 5 minutes, the lead is released back to the marketplace automatically.",
  },
  {
    category: "Purchase Process",
    question: "What contact details do I get after purchasing?",
    answer:
      "Once payment is confirmed, the lead's full contact details unlock immediately in your Purchased Leads tab — including name, phone number, home address, monthly bill range, and purchase timeline. Contact info is hidden until purchase to protect lead quality.",
  },
  {
    category: "Purchase Process",
    question: "Are leads exclusive once I buy them?",
    answer:
      "Yes — 100% exclusive. The moment you purchase a lead, it's permanently removed from the marketplace. No other installer will ever see or be able to buy that same lead. One lead, one installer, always.",
  },
  {
    category: "Pipeline",
    question: "How does the lead pipeline work?",
    answer:
      "In your Purchased Leads tab, each lead has a pipeline status you can update: New → Contacted → Qualified → Appointment → Closed. Click any status button on a lead card to move it forward. You can also add private notes to each lead to track conversations, follow-ups, or any details you want to remember.",
  },
  {
    category: "Settings",
    question: "Can I limit which states and ZIP codes I receive leads from?",
    answer:
      "Yes — in your Settings tab, you can select exactly which states you serve and optionally narrow down to specific ZIP codes. The marketplace will only show you leads from your selected service area. You can update these preferences anytime without affecting leads you've already purchased.",
  },
  {
    category: "Billing",
    question: "How does billing work?",
    answer:
      "You pay per lead at the time of purchase — no monthly fees, no retainers. Prices range from $100 to $200 per lead depending on score and location. Your Billing tab shows a full transaction history including date, lead location, Stripe session ID, and amount paid. All payments are processed securely through Stripe.",
  },
  {
    category: "General",
    question: "What states do you currently serve?",
    answer:
      "We currently serve the Northeast: New Jersey, New York, Pennsylvania, Connecticut, Delaware, Maryland, Massachusetts, and Rhode Island. More states are being added — sign up to get notified when your state goes live.",
  },
];

const categoryColors: Record<string, string> = {
  Dashboard: "bg-green-100 text-green-700",
  "Lead Scoring": "bg-amber-100 text-amber-700",
  "Purchase Process": "bg-sky-100 text-sky-700",
  Pipeline: "bg-violet-100 text-violet-700",
  Settings: "bg-orange-100 text-orange-700",
  Billing: "bg-teal-100 text-teal-700",
  General: "bg-gray-100 text-gray-600",
};

const FILTERS = ["All", "Dashboard", "Lead Scoring", "Purchase Process", "Pipeline", "Settings", "Billing"];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeFilter, setActiveFilter] = useState("All");

  const filtered = activeFilter === "All" ? faqs : faqs.filter(f => f.category === activeFilter);

  const toggle = (i: number) => setOpenIndex(openIndex === i ? null : i);

  return (
    <section id="faq" className="py-24 md:py-32 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-10">
          <span className="text-green-600 text-xs font-bold uppercase tracking-widest mb-3 block">FAQ</span>
          <h2 className="text-3xl md:text-5xl font-bold text-black tracking-tight">Frequently Asked Questions</h2>
          <div className="w-16 h-1 bg-green-500 rounded-full mx-auto mt-4"></div>
          <p className="mt-5 text-gray-500 text-base max-w-xl mx-auto">
            Everything you need to know about the dashboard, lead scoring, and the purchase process.
          </p>
        </div>

        {/* Filter tabs */}
        <div className="flex flex-wrap gap-2 justify-center mb-10">
          {FILTERS.map(f => (
            <button
              key={f}
              onClick={() => { setActiveFilter(f); setOpenIndex(null); }}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition cursor-pointer whitespace-nowrap ${
                activeFilter === f
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-400"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Accordion */}
        <div className="flex flex-col gap-3">
          {filtered.map((faq, i) => (
            <div
              key={`${faq.category}-${i}`}
              className={`border rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer ${
                openIndex === i ? "border-green-300 bg-green-50/40" : "border-gray-200 bg-white hover:border-green-200"
              }`}
              onClick={() => toggle(i)}
            >
              <div className="flex items-center justify-between px-6 py-5 gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`hidden sm:inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${categoryColors[faq.category]}`}>
                    {faq.category}
                  </span>
                  <span className="text-base font-semibold text-black">{faq.question}</span>
                </div>
                <div className="w-7 h-7 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-xl transition-transform duration-300 ${
                      openIndex === i ? "ri-subtract-line text-green-600" : "ri-add-line text-gray-400"
                    }`}
                  ></i>
                </div>
              </div>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === i ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <p className="px-6 pb-5 text-gray-600 text-sm md:text-base leading-relaxed">{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <p className="text-center text-sm text-gray-400 mt-10">
          Still have questions?{" "}
          <a href="mailto:support@solarleadsource.com" className="text-[#16a34a] font-medium hover:underline">
            Email our team
          </a>
        </p>
      </div>
    </section>
  );
}
